import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Lock, Printer } from "lucide-react";
import { OtpInput } from "./OtpInput";
import { confirmMfaEnrol, enrolMfa } from "../../shared/api/auth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";

/**
 * MFA enrolment. On mount we start enrolment (`POST /mfa/enrol`) to get the TOTP secret + otpauth
 * URI, render a real QR, and confirm with a fresh code (`POST /mfa/enrol/confirm`). Recovery codes
 * are not issued by the backend yet, so the final step is a simple confirmation.
 */
export function MfaSetupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"scan" | "done">("scan");
  const [secret, setSecret] = useState("");
  const [otpAuthUri, setOtpAuthUri] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  useEffect(() => {
    enrolMfa()
      .then((info) => {
        setSecret(info.secret);
        setOtpAuthUri(info.otpAuthUri);
      })
      .catch(() =>
        setLoadError(
          "Không khởi tạo được MFA. Vui lòng đăng nhập rồi mở lại trang thiết lập."
        )
      );
  }, []);

  async function onConfirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (code.length !== 6) {
      setError("Vui lòng nhập đủ 6 số.");
      return;
    }
    setSubmitting(true);
    try {
      const codes = await confirmMfaEnrol(code);
      setRecoveryCodes(codes);
      setStep("done");
    } catch {
      setError("Mã không đúng hoặc đã hết hạn. Vui lòng thử lại.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4 font-sans">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 text-3xl font-extrabold tracking-tight text-primary">
            <span aria-hidden="true">✚</span> Addy HIS
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Hệ thống quản lý phòng khám
          </p>
        </div>

        {step === "done" ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Đã bật xác thực 2 lớp</CardTitle>
              <CardDescription>
                Từ lần đăng nhập sau, bạn sẽ cần nhập mã 6 số từ ứng dụng xác
                thực.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recoveryCodes.length > 0 && (
                  <>
                    <p className="text-sm text-foreground">
                      <strong>Lưu các mã khôi phục dưới đây ngay.</strong> Mỗi mã
                      dùng được 1 lần khi mất thiết bị — sẽ không hiển thị lại.
                    </p>
                    <div className="grid grid-cols-2 gap-2 rounded-md border border-input bg-muted p-3">
                      {recoveryCodes.map((rc) => (
                        <code
                          key={rc}
                          className="text-center font-mono text-sm text-foreground"
                        >
                          {rc}
                        </code>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => window.print()}
                    >
                      <Printer />
                      In mã khôi phục
                    </Button>
                  </>
                )}
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  Hãy giữ ứng dụng xác thực an toàn. Mã khôi phục giúp bạn đăng
                  nhập khi mất thiết bị.
                </div>
                <Button
                  type="button"
                  className="w-full"
                  onClick={() => navigate("/dashboard")}
                >
                  Đã lưu, vào hệ thống
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Thiết lập xác thực 2 lớp</CardTitle>
              <CardDescription>
                Quét mã QR bằng <strong>Google Authenticator</strong>,{" "}
                <strong>Microsoft Authenticator</strong>, hoặc{" "}
                <strong>Authy</strong>.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadError && (
                <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {loadError}
                </div>
              )}

              <form className="space-y-4" onSubmit={onConfirm} noValidate>
                <div className="flex justify-center py-2">
                  {otpAuthUri ? (
                    <div className="rounded-md border border-input bg-white p-3">
                      <QRCodeSVG
                        value={otpAuthUri}
                        size={176}
                        includeMargin={false}
                      />
                    </div>
                  ) : (
                    <div className="p-6 text-sm text-muted-foreground">
                      Đang tạo mã QR…
                    </div>
                  )}
                </div>

                <div className="space-y-1.5 rounded-md bg-muted p-3">
                  <div className="text-xs text-muted-foreground">
                    Không quét được? Nhập thủ công mã bí mật bên dưới vào ứng
                    dụng:
                  </div>
                  <div className="break-all text-center font-mono text-sm font-semibold tracking-wider text-foreground">
                    {secret || "…"}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Nhập mã 6 số từ ứng dụng</Label>
                  <OtpInput
                    value={code}
                    onChange={setCode}
                    disabled={submitting || !otpAuthUri}
                    hasError={!!error}
                  />
                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting || !otpAuthUri}
                >
                  {submitting ? "Đang xác nhận…" : "Xác nhận và bật MFA"}
                </Button>

                <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
                  <Lock className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                  <div>
                    MFA giúp bảo vệ dữ liệu bệnh nhân kể cả khi mật khẩu bị rò
                    rỉ. Chủ phòng khám và bác sĩ bắt buộc; lễ tân khuyến nghị.
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
